import { IconNames } from '@dixa/lib-shared-habla';
import { PureComponent } from 'react';
import { v4 as uuid } from 'uuid';

import { FlowType } from '../flowEditorChrome';
import { FlowNode, flowNodeColor, FlowNodeColorValue } from '../flowNode';

export type ActionNodeT = NodeT<'action', null>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ExtraDataT = any;

export interface FlowManagerEditModalComponentT<
  TNode = NodeT,
  TExtraData = ExtraDataT,
> {
  node: TNode | ActionNodeT;
  nodeLabel: string;
  replaceNode: (newNode: TNode, nodeState?: StateNodesT | null) => void;
  triggerClose: () => void;
  nodes: StateNodesT;
  updateConnectionLabel: (
    nodeState: StateNodesT | null,
    node: TNode,
    oldLabel: string,
    newLabel: string,
  ) => StateNodesT;
  ports: InternalPortT[];
  isEdit: boolean;
  isCreate: boolean;
  scopeVars: ScopeObjT;
  extraData: TExtraData;
  flowType: FlowType;
}

export interface FlowManagerNodeComponentT<
  TNode = NodeT,
  TExtraData = ExtraDataT,
> {
  node: TNode;
  scopeVars: ScopeObjT;
  hasError: boolean;
  extraData: TExtraData;
  onEdit: (node: TNode, type: string) => void;
}

export type RegisterPortT = {
  id: string;
  type: 'in' | 'out';
  label: string | null;
  connection: string | null;
};

export type InternalPortT = {
  offsetX: number;
  offsetY: number;
  placement: 'top' | 'bottom';
} & RegisterPortT;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type NodeT<TNode = string | any, TPayload = null | any> = {
  id: string;
  nodeType: TNode;
  pageY: number;
  pageX: number;
  parentId: string | null;
  text?: string;
  connectionLabel: string;
  payload: TPayload;
  isRemovable: boolean;
  isEditable: boolean;
  childIds: Array<string>;
  isAction?: boolean; // legacy - validated by BE - but not needed for FlowBuilder
  icon?: string | null; // legacy - validated by BE - but not needed for FlowBuilder
  badges?: Array<unknown>; // legacy - validated by BE - but not needed for FlowBuilder
  overlay?: Array<unknown>; // legacy - validated by BE - but not needed for FlowBuilder
};

export interface NodeDataT<TActions = NodeT[]> {
  id: string;
  org_id?: string;
  name: string;
  number?: string;
  active: boolean;
  actions: TActions;
  created_at: string;
  updated_at: string;
  location?: string;
  channel?: string;
}

export type ConnectionT = {
  id: string;
  startId: string;
  start: {
    x: number;
    y: number;
    offsetX: number;
    edge: 'bottom' | 'top';
  };
  endId: string;
  end: {
    x: number;
    y: number;
    offsetX: number;
    edge: 'top' | 'bottom';
  };
  label: string;
  highlight: boolean;
  faded: boolean;
  error: boolean;
};

export type ActionTriggerT = {
  type: string;
  helpText?: string;
};

export type PortFormatterT<TNode extends NodeT = NodeT> = (
  node: TNode,
) => RegisterPortT[];

export const DEFAULT_PORTS_IN: PortFormatterT = <TNode extends NodeT>(
  node: TNode,
): RegisterPortT[] => [
  {
    id: node.parentId || uuid(),
    type: 'in',
    connection: node.parentId,
    label: node.connectionLabel,
  },
];
export const DEFAULT_PORTS_OUT: PortFormatterT = <TNode extends NodeT>(
  _node: TNode,
): RegisterPortT[] => [];

export const formatPortsHelper =
  <TNode extends NodeT>(
    outPortFormatter: PortFormatterT<TNode> = DEFAULT_PORTS_OUT,
    inPortFormater: PortFormatterT<TNode> = DEFAULT_PORTS_IN,
  ): PortFormatterT<TNode> =>
  (node: TNode) => {
    return [...outPortFormatter(node), ...inPortFormater(node)];
  };

export function NodeTypeComponent<
  TNode extends NodeT,
  TExtraData = Record<string, unknown>,
  TState = Record<string, unknown>,
>() {
  class NodeTypeComponent extends PureComponent<
    FlowManagerNodeComponentT<TNode, TExtraData>,
    TState
  > {
    static icon: IconNames;
    static label: string;
    static color: FlowNodeColorValue;
    static helpText?: React.ReactChild;
    static helpTextTitle?: React.ReactChild;
    static height: number;
    static width: number;
    static disabled = false;
    static badgeContent?: React.ReactChild;
    static translatePayload: (node: TNode) => RegisterPortT[];
    static translateScopeVariables?: (node: TNode) => ScopeVarsT;
    static updateConnection: (
      node: TNode,
      port: InternalPortT,
      nodeId: NodeT['id'],
    ) => TNode;
    static ActionModal: null | React.ComponentType<
      // Because we use various HOCs, ReduxConnect, ReduxForm etc. we can't make this strict
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FlowManagerEditModalComponentT<TNode, TExtraData> & any
    >;
    static actionTrigger?: ActionTriggerT;
  }

  // Set defaults
  NodeTypeComponent.icon = 'dixa';
  NodeTypeComponent.label = 'Unknown';
  NodeTypeComponent.color = flowNodeColor.default;
  NodeTypeComponent.height = FlowNode.dimensions.h;
  NodeTypeComponent.width = FlowNode.dimensions.w;
  NodeTypeComponent.translatePayload = formatPortsHelper<TNode>();
  NodeTypeComponent.updateConnection = (
    node: TNode,
    _port: InternalPortT,
    _nodeId: TNode['id'],
  ): TNode => node;
  // no default
  NodeTypeComponent.actionTrigger = undefined;
  NodeTypeComponent.translateScopeVariables = undefined;
  NodeTypeComponent.ActionModal = null;

  return NodeTypeComponent;
}

export type NodeTypeComponentT = ReturnType<typeof NodeTypeComponent>;
export type NodeTypeComponentsListT = Record<string, NodeTypeComponentT>;

export type NodeTypeT<TNode, TExtraData> = React.ComponentType<
  FlowManagerNodeComponentT<TNode, TExtraData>
> & {
  icon: IconNames;
  label: string;
  color: FlowNodeColorValue;
  helpText?: string;
  height: number;
  width: number;
  translatePayload: (node: TNode) => RegisterPortT[];
  translateScopeVariables?: (node: TNode) => ScopeVarsT;
  updateConnection: (
    parentNode: NodeT,
    port: InternalPortT,
    nodeId: NodeT['id'],
  ) => NodeT;
  ActionModal?: React.ComponentType<
    FlowManagerEditModalComponentT<TNode, TExtraData>
  >;
  actionTrigger?: {
    type: string;
    helpText?: string;
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface InternalNodeT<TNode = string | any, TPL = null | any>
  extends NodeT<TNode, TPL> {
  isDetachedFromRoot: boolean;
}

export type StateNodesT = {
  [K in string]: InternalNodeT;
};

export type ExternalNodesT = {
  [K in string]: NodeT;
};

export type StatePortsT = {
  [K in string]: Array<InternalPortT>;
};

export type ScopeObjT = {
  pathID: string;
  needs: string[];
  descendantNeeds: string[];
  available: string[];
  grants: string[];
  existing: string[];
  ownNeeds: string[];
};

export type ScopeObjMapT = Record<string, ScopeObjT>;

export type ScopeVarsT = {
  grants: string[];
  needs: string[];
};

export type ScopeVarsMapT = {
  [K in string]: ScopeVarsT;
};
